import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, AccountTree as RelationshipIcon, AutoAwesome as AutoAwesomeIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { Technology, TechnologyCreate, TechnologyUpdate, TechnologyClassification, TechnologyListItem } from '../../../types/technology';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useAuth } from '../../../hooks/useAuth';
import { useEnvironment } from '../../../hooks/useEnvironment';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import ConfirmationDialog from '../../safety/ConfirmationDialog/ConfirmationDialog';
import ReviewChangesDialog from '../../safety/ReviewChangesDialog/ReviewChangesDialog';
import { mapErrorToMessage } from '../../../utils/error-mapping';
import { enrichTechnology, searchTechnologies, addParentTechnology, removeParentTechnology, addChildTechnology, removeChildTechnology } from '../../../api/technologies';

interface TechnologyFormProps {
  technology?: Technology | null;
  onSave: (data: TechnologyCreate | TechnologyUpdate, id?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onCancel?: () => void;
}

const TechnologyForm: React.FC<TechnologyFormProps> = ({
  technology,
  onSave,
  onDelete,
  onCancel
}) => {
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const isViewer = user?.role === 'viewer';
  const isProduction = environment === 'production';

  const [formData, setFormData] = useState<TechnologyCreate>({
    name: '',
    classification: undefined,
    content_archive_url: '',
    description: '',
    summary_description: '',
    metadata_description: '',
    attribution_statement: '',
    completion_score: 0,
  });

  const [parents, setParents] = useState<Array<{ id: string; name: string }>>([]);
  const [children, setChildren] = useState<Array<{ id: string; name: string }>>([]);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [childSearchQuery, setChildSearchQuery] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState<TechnologyListItem[]>([]);
  const [childSearchResults, setChildSearchResults] = useState<TechnologyListItem[]>([]);
  const [searchingParents, setSearchingParents] = useState(false);
  const [searchingChildren, setSearchingChildren] = useState(false);
  const [managingRelation, setManagingRelation] = useState(false);

  const [originalData, setOriginalData] = useState<Technology | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const { errors, validateAll, clearAllErrors } = useFormValidation({
    name: { required: true, minLength: 1 }
  });

  useEffect(() => {
    if (technology) {
      const data = {
        name: technology.name || '',
        classification: technology.classification,
        content_archive_url: technology.content_archive_url || '',
        description: technology.description || '',
        summary_description: technology.summary_description || '',
        metadata_description: technology.metadata_description || '',
        attribution_statement: technology.attribution_statement || '',
        completion_score: technology.completion_score || 0,
      };
      setFormData(data);
      setParents(technology.parent_technologies || []);
      setChildren(technology.child_technologies || []);
      setOriginalData(technology);
    } else {
      setParents([]);
      setChildren([]);
      setOriginalData(null);
    }
  }, [technology]);

  const handleChange = (field: keyof TechnologyCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEnrich = async () => {
    if (!technology?.id) return;

    setEnriching(true);
    setError(null);

    try {
      const result = await enrichTechnology(technology.id);

      if (result.error) {
        throw new Error(result.error);
      }

      if (!result.technology) {
        throw new Error("No enriched data returned");
      }

      setFormData(prev => ({
        ...prev,
        summary_description: result.technology.summary_description || prev.summary_description,
        description: result.technology.description || prev.description,
        metadata_description: result.technology.metadata_description || prev.metadata_description,
      }));

    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to enrich technology'));
    } finally {
      setEnriching(false);
    }
  };

  const handleParentSearch = async (query: string) => {
    setParentSearchQuery(query);

    if (!query.trim()) {
      setParentSearchResults([]);
      return;
    }

    setSearchingParents(true);

    try {
      const results = await searchTechnologies(query);
      const filteredResults = results.filter(
        r => r.id !== technology?.id && !parents.some(p => p.id === r.id)
      );
      setParentSearchResults(filteredResults);
    } catch {
      setParentSearchResults([]);
    } finally {
      setSearchingParents(false);
    }
  };

  const handleAddParent = async (parent: TechnologyListItem) => {
    if (!technology?.id) return;

    setManagingRelation(true);
    setError(null);

    try {
      await addParentTechnology(technology.id, parent.id);
      setParents(prev => [...prev, { id: parent.id, name: parent.name }]);
      setParentSearchQuery('');
      setParentSearchResults([]);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to add parent technology'));
    } finally {
      setManagingRelation(false);
    }
  };

  const handleRemoveParent = async (parentId: string) => {
    if (!technology?.id) return;

    setManagingRelation(true);
    setError(null);

    try {
      await removeParentTechnology(technology.id, parentId);
      setParents(prev => prev.filter(p => p.id !== parentId));
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to remove parent technology'));
    } finally {
      setManagingRelation(false);
    }
  };

  const handleChildSearch = async (query: string) => {
    setChildSearchQuery(query);

    if (!query.trim()) {
      setChildSearchResults([]);
      return;
    }

    setSearchingChildren(true);

    try {
      const results = await searchTechnologies(query);
      const filteredResults = results.filter(
        r => r.id !== technology?.id && !children.some(c => c.id === r.id)
      );
      setChildSearchResults(filteredResults);
    } catch {
      setChildSearchResults([]);
    } finally {
      setSearchingChildren(false);
    }
  };

  const handleAddChild = async (child: TechnologyListItem) => {
    if (!technology?.id) return;

    setManagingRelation(true);
    setError(null);

    try {
      await addChildTechnology(technology.id, child.id);
      setChildren(prev => [...prev, { id: child.id, name: child.name }]);
      setChildSearchQuery('');
      setChildSearchResults([]);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to add child technology'));
    } finally {
      setManagingRelation(false);
    }
  };

  const handleRemoveChild = async (childId: string) => {
    if (!technology?.id) return;

    setManagingRelation(true);
    setError(null);

    try {
      await removeChildTechnology(technology.id, childId);
      setChildren(prev => prev.filter(c => c.id !== childId));
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to remove child technology'));
    } finally {
      setManagingRelation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    if (isProduction) {
      setShowReviewDialog(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    setLoading(true);
    setError(null);
    clearAllErrors();
    setShowReviewDialog(false);

    try {
      await onSave(formData, technology?.id);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to save technology'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!technology?.id || !onDelete) return;

    setLoading(true);
    setError(null);

    try {
      await onDelete(technology.id);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to delete technology'));
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with Title and Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {technology ? 'Edit Technology' : 'New Technology'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {technology && !isViewer && (user?.role === 'editor' || user?.role === 'admin') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={enriching ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              onClick={handleEnrich}
              disabled={enriching || loading}
            >
              {enriching ? 'Enriching...' : 'Enrich with AI'}
            </Button>
          )}
          {technology && onDelete && !isViewer && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading || enriching}
            >
              Delete Technology
            </Button>
          )}
        </Box>
      </Box>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isViewer}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID"
                value={technology?.id || ''}
                disabled
                helperText="Unique identifier (auto-generated)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={formData.classification || ''}
                  onChange={(e) => handleChange('classification', e.target.value || undefined)}
                  disabled={isViewer}
                  label="Classification"
                >
                  <MenuItem value="">
                    <em>Select classification...</em>
                  </MenuItem>
                  {Object.values(TechnologyClassification).map(cls => (
                    <MenuItem key={cls} value={cls}>
                      {cls.replace('_', ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content Archive URL"
                value={formData.content_archive_url}
                onChange={(e) => handleChange('content_archive_url', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Summary Description"
                value={formData.summary_description}
                onChange={(e) => handleChange('summary_description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Detailed Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Metadata Description (for LLM context)"
                value={formData.metadata_description}
                onChange={(e) => handleChange('metadata_description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Additional Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Attribution Statement"
                value={formData.attribution_statement}
                onChange={(e) => handleChange('attribution_statement', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Completion Score (0-100)"
                value={formData.completion_score}
                onChange={(e) => handleChange('completion_score', parseInt(e.target.value) || 0)}
                disabled={isViewer}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Relationships Section */}
        {technology && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <RelationshipIcon />
              Technology Relationships
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4}>
              {/* Parent Technologies */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Parent Technologies
                </Typography>

                {parents.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
                    No parent technologies set.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {parents.map(p => (
                      <Chip
                        key={p.id}
                        label={p.name}
                        onDelete={!isViewer ? () => handleRemoveParent(p.id) : undefined}
                        color="primary"
                        variant="outlined"
                        disabled={managingRelation}
                      />
                    ))}
                  </Box>
                )}

                {!isViewer && (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search technologies to add as parent..."
                      value={parentSearchQuery}
                      onChange={(e) => handleParentSearch(e.target.value)}
                      disabled={managingRelation}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchingParents && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {parentSearchResults.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          maxHeight: 250,
                          overflowY: 'auto',
                        }}
                      >
                        <List disablePadding>
                          {parentSearchResults.map((tech, index) => (
                            <ListItem
                              key={tech.id}
                              sx={{
                                borderBottom: index < parentSearchResults.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                              onClick={() => handleAddParent(tech)}
                              disabled={managingRelation}
                            >
                              <ListItemText
                                primary={tech.name}
                                secondary={tech.classification}
                              />
                              <IconButton edge="end" color="primary" size="small" disabled={managingRelation}>
                                <AddIcon />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {parentSearchQuery && !searchingParents && parentSearchResults.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1 }}>
                        No technologies found matching "{parentSearchQuery}"
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Child Technologies */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Child Technologies
                </Typography>

                {children.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
                    No child technologies set.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {children.map(c => (
                      <Chip
                        key={c.id}
                        label={c.name}
                        onDelete={!isViewer ? () => handleRemoveChild(c.id) : undefined}
                        color="secondary"
                        variant="outlined"
                        disabled={managingRelation}
                      />
                    ))}
                  </Box>
                )}

                {!isViewer && (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search technologies to add as child..."
                      value={childSearchQuery}
                      onChange={(e) => handleChildSearch(e.target.value)}
                      disabled={managingRelation}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchingChildren && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {childSearchResults.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          maxHeight: 250,
                          overflowY: 'auto',
                        }}
                      >
                        <List disablePadding>
                          {childSearchResults.map((tech, index) => (
                            <ListItem
                              key={tech.id}
                              sx={{
                                borderBottom: index < childSearchResults.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                              onClick={() => handleAddChild(tech)}
                              disabled={managingRelation}
                            >
                              <ListItemText
                                primary={tech.name}
                                secondary={tech.classification}
                              />
                              <IconButton edge="end" color="secondary" size="small" disabled={managingRelation}>
                                <AddIcon />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {childSearchQuery && !searchingChildren && childSearchResults.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1 }}>
                        No technologies found matching "{childSearchQuery}"
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Form Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              size="large"
            >
              Cancel
            </Button>
          )}
          {!isViewer && (
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </form>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Technology"
        message={`Are you sure you want to delete "${technology?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDangerous={true}
        requireDoubleConfirm={isProduction}
        doubleConfirmMessage={`You are about to permanently delete "${technology?.name}" from the PRODUCTION database. This action is irreversible and cannot be undone.`}
      />

      <ReviewChangesDialog
        isOpen={showReviewDialog}
        title="Review Production Changes"
        originalData={originalData}
        modifiedData={{
          ...formData,
          ...(parents.length > 0 ? { parent_technologies: parents } : {}),
          ...(children.length > 0 ? { child_technologies: children } : {}),
        }}
        onConfirm={performSave}
        onCancel={() => setShowReviewDialog(false)}
        entityType="technology"
      />
    </Box>
  );
};

export default TechnologyForm;
